import { castArray, each, get } from 'lodash';

export { color } from '@packages/style/colors';

export type PropParser = {
    (value?: any, props?: { [key: string]: any }): any;
    _receiveProps?: boolean;
};

type Key = string | string[];

interface ParsedArgs {
    keys: Key;
    defaultValue: any;
}

interface ParseArgsOptions {
    useFirstKey: boolean;
    prefix: string;
    parser: PropParser;
}

/**
 *
 * @param keys
 * @param defaultValue
 * @param options
 * @private
 */
function _parseArgs(keys: Key, defaultValue: any, options: Partial<ParseArgsOptions> = {}): ParsedArgs {
    keys = castArray(keys);

    if (options.useFirstKey) {
        if (typeof defaultValue === 'undefined' || defaultValue === null) {
            defaultValue = keys[0];
        }
    }

    if (options.prefix) {
        keys = keys.map((key) => (key.charAt(0) === '@' ? key.replace(/^@/, '') : `${options.prefix}${key}`));
    }

    return { keys, defaultValue };
}

/**
 * Get and return same value without modify
 * @param val
 * @returns {*}
 */
export const defaultParser = (val: any): any => val;

/**
 * Create a function that will get prop or default value and will pass it to a
 * function that can edit the value
 * @param key
 * @param defaultValue
 * @param parser
 * @returns {*}
 */
export const prop =
    (key: string | string[], defaultValue?: any, parser?: any | PropParser) =>
    (props): any => {
        if (typeof parser !== 'function') {
            parser = defaultParser;
        }

        let value = undefined;

        key = castArray(key);

        each(key, (k) => {
            value = get(props, k);
            if (typeof value !== 'undefined') {
                return false;
            }
        });

        if (defaultValue && typeof defaultValue === 'function') {
            parser = defaultValue;
            defaultValue = undefined;
        }

        const _args = [value !== null && typeof value !== 'undefined' ? value : defaultValue];

        if (parser._receiveProps) {
            _args.push(props);
        }

        return parser(..._args);
    };

/**
 * Create Prop getter
 * @param options
 * @returns {function(keys: string[], defaultValue: any, parser: Parser): any}
 */
export function createPropGetter(options: Partial<ParseArgsOptions> = {}): any {
    const fn = (keys: string | string[], defaultValue: any, parser: PropParser = defaultParser): any => {
        options.parser = options.parser || defaultParser;

        if (defaultValue && typeof defaultValue === 'function') {
            parser = defaultValue;
            defaultValue = undefined;
        }

        const args = _parseArgs(keys, defaultValue, options);

        return prop(
            args.keys,
            args.defaultValue,
            receiveProps((value, props) => {
                const _args = [options.parser!(value, props) as any];

                if (parser._receiveProps) {
                    _args.push(props);
                }

                return parser(..._args);
            }),
        );
    };

    fn._receiveProps = true;

    return fn;
}

/**
 * Create a function that will get theme or default value and will pass it to a
 * function that can edit the value
 */
export const theme = createPropGetter({
    prefix: 'theme.',
});

/**
 * Wrap functions
 * @param fn
 * @returns {*}
 */
export const receiveProps = (fn: PropParser): PropParser => {
    fn._receiveProps = true;
    return fn;
};
